import { type Column, type RowData, type Table } from "@tanstack/react-table";
import { ChevronRight, Columns3 } from "lucide-react";
import { useMemo, useState } from "react";
import { Tooltip } from "./Tooltip";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  type DropdownMenuCheckboxItemProps,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface ColumnGroup {
  id: string;
  title: string;
  columnIds: string[];
}

function getColumnHeader<TData extends RowData>(column: Column<TData, unknown>) {
  if (typeof column.columnDef.header === "string") {
    return column.columnDef.header;
  }
  if (typeof column.columnDef.header === "function") {
    return column.id;
  }
  return column.id;
}

function sortColumnsByHeader<TData extends RowData>(columns: Column<TData, unknown>[]) {
  return [...columns].sort((a, b) => {
    const firstHeader = getColumnHeader(a);
    const secondHeader = getColumnHeader(b);
    if (firstHeader < secondHeader) return -1;
    if (firstHeader > secondHeader) return 1;
    return 0;
  });
}

// Pure function to determine if a column should be disabled from hiding
function shouldDisableColumn<TData extends RowData>(
  column: Column<TData, unknown>,
  pinnedColumns: Column<TData, unknown>[],
  columnToKeepVisible: Column<TData, unknown> | undefined,
  totalVisibleColumns: number,
): { disabled: boolean; tooltip?: string } {
  const isPinnedColumn = column.getIsPinned() !== false;
  const isOnlyVisibleColumn = totalVisibleColumns === 1 && column.getIsVisible();
  const isFirstColumn = pinnedColumns.length === 0 && columnToKeepVisible && column.id === columnToKeepVisible.id;

  if (isPinnedColumn) {
    return { disabled: true, tooltip: "Pinned column cannot be hidden" };
  }

  if (isFirstColumn) {
    return { disabled: true, tooltip: "At least one column must remain visible" };
  }

  if (isOnlyVisibleColumn) {
    return { disabled: true, tooltip: "At least one column must remain visible" };
  }

  return { disabled: false };
}

export function ColumnVisibilityToggle<TData extends RowData>({
  table,
  disabled,
  groups,
}: {
  table: Table<TData>;
  disabled: boolean;
  groups?: ColumnGroup[];
}) {
  const hideableColumns = table.getAllColumns().filter((column) => column.getCanHide());

  // Find pinned columns or fallback to first column
  const pinnedColumns = hideableColumns.filter((column) => column.getIsPinned() !== false);
  const columnToKeepVisible = pinnedColumns.length > 0 ? pinnedColumns[0] : hideableColumns[0];
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(groups?.map((g) => g.id) || []));

  const toggleColumns = () => {
    const visibleColumns = table.getVisibleFlatColumns();
    if (visibleColumns.length === 1) {
      table.resetColumnVisibility();
    } else {
      // Always keep pinned columns (or first column if none pinned) visible, hide all others
      table.setColumnVisibility({
        ...hideableColumns.reduce((acc, column) => {
          const isPinned = column.getIsPinned() !== false;
          // If there are pinned columns, keep all pinned columns visible
          // Otherwise, keep the first column visible
          const shouldKeepVisible =
            pinnedColumns.length > 0 ? isPinned : columnToKeepVisible && column.id === columnToKeepVisible.id;
          return shouldKeepVisible ? { ...acc, [column.id]: true } : { ...acc, [column.id]: false };
        }, {}),
      });
    }
  };

  const { groupedColumns, ungroupedColumns } = useMemo(() => {
    if (!groups || groups.length === 0) {
      return { groupedColumns: new Map(), ungroupedColumns: hideableColumns };
    }

    const grouped = new Map<string, Column<TData, unknown>[]>();
    const ungrouped: Column<TData, unknown>[] = [];
    const groupedColumnIds = new Set(groups.flatMap((g) => g.columnIds));

    hideableColumns.forEach((column) => {
      if (groupedColumnIds.has(column.id)) {
        for (const group of groups) {
          if (group.columnIds.includes(column.id)) {
            if (!grouped.has(group.id)) {
              grouped.set(group.id, []);
            }
            grouped.get(group.id)!.push(column);
            break;
          }
        }
      } else {
        ungrouped.push(column);
      }
    });

    return { groupedColumns: grouped, ungroupedColumns: ungrouped };
  }, [hideableColumns, groups]);

  const toggleGroupColumns = (groupId: string) => {
    const columns = groupedColumns.get(groupId) || [];
    if (columns.length === 0) return;

    const allVisible = columns.every((col: Column<TData, unknown>) => col.getIsVisible());
    const totalVisibleColumns = table.getVisibleFlatColumns().length;
    const groupVisibleCount = columns.filter((col: Column<TData, unknown>) => col.getIsVisible()).length;

    // Don't hide if it would leave less than 1 column visible
    if (allVisible && totalVisibleColumns - groupVisibleCount < 1) {
      return;
    }

    const newVisibility: Record<string, boolean> = {};
    columns.forEach((column: Column<TData, unknown>) => {
      // Never hide pinned columns
      if (column.getIsPinned() !== false) {
        newVisibility[column.id] = true;
      } else if (pinnedColumns.length === 0 && columnToKeepVisible && column.id === columnToKeepVisible.id) {
        // Never hide the first column when no pinned columns
        newVisibility[column.id] = true;
      } else {
        // Toggle visibility for other columns
        newVisibility[column.id] = !allVisible;
      }
    });

    table.setColumnVisibility((prev) => ({
      ...prev,
      ...newVisibility,
    }));
  };

  const isGroupAllVisible = (groupId: string) => {
    const columns = groupedColumns.get(groupId) || [];
    if (columns.length === 0) return false;
    return columns.every((col: Column<TData, unknown>) => col.getIsVisible());
  };

  const renderColumnItem = (column: Column<TData, unknown>) => {
    const totalVisibleColumns = table.getVisibleFlatColumns().length;
    const { disabled, tooltip } = shouldDisableColumn(column, pinnedColumns, columnToKeepVisible, totalVisibleColumns);

    const checkboxProps: DropdownMenuCheckboxItemProps = {
      className: "capitalize",
      checked: column.getIsVisible(),
      onSelect: (event: Event) => event.preventDefault(),
      onCheckedChange: (value: boolean) => {
        // Prevent hiding disabled columns
        if (disabled && !value) {
          return;
        }
        column.toggleVisibility(!!value);
      },
      disabled,
    };

    const columnItem = (
      <DropdownMenuCheckboxItem key={column.id} {...checkboxProps} className="cursor-pointer">
        {getColumnHeader(column)}
      </DropdownMenuCheckboxItem>
    );

    return tooltip ? (
      <Tooltip key={column.id} label={tooltip} side="left">
        {columnItem}
      </Tooltip>
    ) : (
      columnItem
    );
  };

  const sortedUngrouped = sortColumnsByHeader(ungroupedColumns);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="ml-auto" disabled={disabled}>
          <Columns3 className="h-4 w-4" /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="left" className="max-h-[80vh] overflow-y-auto">
        <DropdownMenuCheckboxItem
          onSelect={(event) => event.preventDefault()}
          onCheckedChange={toggleColumns}
          className="font-semibold"
        >
          {table.getVisibleFlatColumns().length === 1 ? "Show All" : "Hide"}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />

        {groups && groups.length > 0 ? (
          <>
            {groups.map((group) => {
              const groupColumns = groupedColumns.get(group.id) || [];
              if (groupColumns.length === 0) return null;

              const sortedGroupColumns = sortColumnsByHeader(groupColumns);
              const allVisible = isGroupAllVisible(group.id);
              const isOpen = openGroups.has(group.id);

              return (
                <Collapsible
                  key={group.id}
                  open={isOpen}
                  onOpenChange={(open) => {
                    setOpenGroups((prev) => {
                      const next = new Set(prev);
                      if (open) {
                        next.add(group.id);
                      } else {
                        next.delete(group.id);
                      }
                      return next;
                    });
                  }}
                >
                  <div className="flex items-center">
                    <CollapsibleTrigger asChild className="flex-1">
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                        }}
                        className="font-medium flex items-center justify-between"
                      >
                        <span>{group.title}</span>
                        <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </DropdownMenuItem>
                    </CollapsibleTrigger>
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        toggleGroupColumns(group.id);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground w-10 mx-1"
                    >
                      {allVisible ? "Hide" : "Show"}
                    </DropdownMenuItem>
                  </div>
                  <CollapsibleContent className="pl-4">
                    {sortedGroupColumns.map((column) => renderColumnItem(column as Column<TData, unknown>))}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
            {sortedUngrouped.length > 0 && <DropdownMenuSeparator />}
            {sortedUngrouped.map((column) => renderColumnItem(column))}
          </>
        ) : (
          sortColumnsByHeader(hideableColumns).map((column) => renderColumnItem(column))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
